import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjsUtils from '@attain-apps/rxjs-utils'
import * as rxjs from 'rxjs'

import type * as dms from '../../../../dms'
import type { Plugin } from '../plugin'

/**
 * Waits for the DMS models, samples the current context and schema, then
 * creates one executable request for every applicable entity.
 */
export const loadOptions = store.effects.create(
  <TTypeParams extends Plugin.TypeParams>(
    feature: Plugin<TTypeParams>['feature'],
    plugin: Plugin<TTypeParams>,
    schemaFeature: dms.schema.Feature,
  ) =>
    feature.actions.loadOptions.observe().pipe(
      rxjsUtils.latestFrom(
        action => ({
          action: rxjs.of(action),
          context: plugin.collection.feature.selectors.context.asObservable,
          schema: schemaFeature.selectors.schema.asObservable,
        }),
        ({ schema }) => schema.areModelsInitialized,
      ),
      rxjs.switchMap(({ action, context, schema }) => {
        if (!context || !schema.tryGetDimension(plugin.dimensionId)) {
          return rxjs.EMPTY
        }

        const entityIds = new Set(
          schema
            .getEntitiesInDimension(plugin.dimensionId)
            .map(entity => entity.id),
        )
        const requestedCriteria = action.data?.criteria
        const criteriaList = requestedCriteria
          ? [requestedCriteria].filter(criteria =>
              entityIds.has(criteria.entityId),
            )
          : Array.from(entityIds, entityId => ({ entityId }))

        return rxjs.from(criteriaList).pipe(
          rxjs.map(criteria =>
            feature.actions.load.request.from(action).create({
              context,
              criteria,
            }),
          ),
        )
      }),
    ),
)
